import { z } from 'zod';
import { AccountStatus } from './accounts.js';

export const OperationQueueSummary = z.object({
  pending: z.number().int().nonnegative(),
  inflight: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  nextAttemptAt: z.number().int().nullable(),
  oldestPendingAt: z.number().int().nullable(),
});
export type OperationQueueSummary = z.infer<typeof OperationQueueSummary>;

export const OutboxSummary = z.object({
  sentCopyPending: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type OutboxSummary = z.infer<typeof OutboxSummary>;

export const SyncStrategy = z.enum(['basic', 'condstore', 'qresync']);
export type SyncStrategy = z.infer<typeof SyncStrategy>;

export const SyncFolderStatus = z.object({
  folderId: z.number().int().positive(),
  name: z.string(),
  path: z.string(),
  specialUse: z.string().nullable(),
  syncEnabled: z.boolean(),
  backfillDone: z.boolean(),
  providerMessageCount: z.number().int().nonnegative(),
  enumerableMessageCount: z.number().int().nonnegative().nullable(),
  mirroredMessageCount: z.number().int().nonnegative(),
  missingMessageCount: z.number().int().nonnegative(),
  missingUids: z.array(z.number().int().positive()).max(25),
  providerUnavailableMessageCount: z.number().int().nonnegative(),
  providerUnavailableUids: z.array(z.number().int().positive()).max(25),
  lastError: z.string().nullable(),
  lastSyncedAt: z.number().int().nullable(),
  lastReconciledAt: z.number().int().nullable(),
  strategy: SyncStrategy,
});
export type SyncFolderStatus = z.infer<typeof SyncFolderStatus>;

export const SyncAccountStatus = z.object({
  accountId: z.number().int().positive(),
  name: z.string(),
  email: z.string(),
  color: z.string(),
  enabled: z.boolean(),
  state: AccountStatus,
  error: z.string().nullable(),
  lastSyncedAt: z.number().int().nullable(),
  folderCount: z.number().int().nonnegative(),
  backfillFoldersRemaining: z.number().int().nonnegative(),
  rebindMessagesRemaining: z.number().int().nonnegative(),
  providerMessageCount: z.number().int().nonnegative(),
  enumerableMessageCount: z.number().int().nonnegative(),
  missingMessageCount: z.number().int().nonnegative(),
  providerUnavailableMessageCount: z.number().int().nonnegative(),
  mirroredMessageCount: z.number().int().nonnegative(),
  lastReconciledAt: z.number().int().nullable(),
  queue: OperationQueueSummary,
  outbox: OutboxSummary,
  folders: z.array(SyncFolderStatus),
});
export type SyncAccountStatus = z.infer<typeof SyncAccountStatus>;

export const FailedOperation = z.object({
  id: z.number().int().positive(),
  accountId: z.number().int().positive(),
  kind: z.string(),
  description: z.string(),
  messageId: z.number().int().positive().nullable(),
  threadId: z.number().int().positive().nullable(),
  subject: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  error: z.string(),
  createdAt: z.number().int(),
});
export type FailedOperation = z.infer<typeof FailedOperation>;

export const SyncStatusResponse = z.object({
  generatedAt: z.number().int(),
  accounts: z.array(SyncAccountStatus),
  failedOperations: z.array(FailedOperation),
});
export type SyncStatusResponse = z.infer<typeof SyncStatusResponse>;

export const SyncRefreshRequest = z
  .object({ accountId: z.number().int().positive().optional() })
  .strict();
export type SyncRefreshRequest = z.infer<typeof SyncRefreshRequest>;

export const SyncRefreshResponse = z.object({
  ok: z.literal(true),
  accountIds: z.array(z.number().int().positive()),
});
export type SyncRefreshResponse = z.infer<typeof SyncRefreshResponse>;

export const SyncReconcileRequest = z
  .object({
    accountId: z.number().int().positive(),
    folderId: z.number().int().positive().optional(),
  })
  .strict();
export type SyncReconcileRequest = z.infer<typeof SyncReconcileRequest>;

export const SyncReconcileResponse = z.object({
  ok: z.literal(true),
  accountId: z.number().int().positive(),
  folderIds: z.array(z.number().int().positive()),
});
export type SyncReconcileResponse = z.infer<typeof SyncReconcileResponse>;

export const OperationRetryResponse = z.object({
  ok: z.literal(true),
  operationId: z.number().int().positive(),
  state: z.literal('pending'),
});
export type OperationRetryResponse = z.infer<typeof OperationRetryResponse>;

export const OperationAcknowledgeResponse = z.object({
  ok: z.literal(true),
  acknowledged: z.number().int().nonnegative(),
});
export type OperationAcknowledgeResponse = z.infer<typeof OperationAcknowledgeResponse>;
